import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

/**
 * PlayerContext — Persistent global audio engine for VoxSmyth.
 *
 * Owns a single `<audio>` element rendered at the AppRouter root that survives
 * route changes. This lets the mini-player + full-player modal show LIVE
 * playback state (scrubber, duration, isPlaying) from any route without the
 * page-component-scoped audio unmounting.
 *
 * Scope deliberately limited to single-file audio. ChunkedAudioManager for
 * multi-voice books still lives inside EnhancedPlayerPage; in that mode the
 * modal falls back to the static-companion behavior.
 *
 * Consumer contract (EnhancedPlayerPage):
 *   const { audioRef, setNowPlaying, setAudioCallbacks } = usePlayer();
 *   useEffect(() => setNowPlaying({ bookId, title, chapter, cover, chapters }), [...]);
 *   useEffect(() => setAudioCallbacks({ onEnded: handleEnded, onTimeUpdate, onPause }), [...]);
 *   // audioRef.current is the global <audio>; existing code keeps working.
 */
const PlayerContext = createContext(null);

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  // Returns a no-op stub if used outside provider — keeps tests / SSR safe.
  if (!ctx) {
    return {
      audioRef: { current: null },
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      nowPlaying: null,
      isChunked: false,
      setNowPlaying: () => {},
      setAudioCallbacks: () => {},
      setChunkedManager: () => {},
      play: () => {},
      pause: () => {},
      togglePlay: () => {},
      seekTo: () => {},
      seekRelative: () => {},
      setRate: () => {},
    };
  }
  return ctx;
};

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const callbacksRef = useRef({});

  // When a multi-voice ChunkedAudioManager is active for the current book,
  // it owns its own internal <audio> elements and drives playback. We attach
  // listeners to mirror its state into the provider so the global mini-player
  // + FullPlayerModal scrubber work identically across both engines.
  const chunkedRef = useRef(null);
  const chunkedListenersRef = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [nowPlaying, setNowPlayingState] = useState(null);
  const [isChunked, setIsChunked] = useState(false);

  // Stable setter that also mirrors to sessionStorage so legacy code reading
  // `vs_now_playing` still works during the migration window.
  const setNowPlaying = useCallback((np) => {
    setNowPlayingState(np);
    try {
      if (np && np.bookId) {
        sessionStorage.setItem('vs_now_playing', JSON.stringify({
          bookId: np.bookId,
          title: np.title || '',
          chapter: np.chapter || '',
          cover: np.cover || '',
          updatedAt: Date.now(),
        }));
      } else {
        sessionStorage.removeItem('vs_now_playing');
      }
      window.dispatchEvent(new Event('vs-now-playing-changed'));
    } catch (_) { /* ignore quota / parse */ }
  }, []);

  // Page-side handlers can hook into audio events (onEnded for auto-chapter,
  // onTimeUpdate for progress save, onPause for save-now). Registering REPLACES
  // (not appends) — pages reset callbacks on unmount via empty object.
  const setAudioCallbacks = useCallback((cbs) => {
    callbacksRef.current = cbs || {};
  }, []);

  const play = useCallback(async () => {
    try {
      if (chunkedRef.current) await chunkedRef.current.play();
      else await audioRef.current?.play();
    } catch (_) { /* play promise rejected */ }
  }, []);
  const pause = useCallback(() => {
    try {
      if (chunkedRef.current) chunkedRef.current.pause();
      else audioRef.current?.pause();
    } catch (_) { /* noop */ }
  }, []);
  const togglePlay = useCallback(() => {
    if (chunkedRef.current) {
      // ChunkedAudioManager doesn't expose a `paused` getter consistently;
      // rely on the provider's mirrored `isPlaying` state instead.
      if (isPlaying) chunkedRef.current.pause();
      else chunkedRef.current.play().catch(() => {});
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) play();
    else pause();
  }, [play, pause, isPlaying]);

  const seekTo = useCallback((t) => {
    if (chunkedRef.current && typeof chunkedRef.current.seekTo === 'function') {
      try { chunkedRef.current.seekTo(Math.max(0, t)); } catch (_) { /* noop */ }
      setCurrentTime(t);
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    const next = Number.isFinite(a.duration) ? Math.max(0, Math.min(a.duration, t)) : Math.max(0, t);
    a.currentTime = next;
    setCurrentTime(next);
  }, []);

  const seekRelative = useCallback((delta) => {
    if (chunkedRef.current && typeof chunkedRef.current.seek === 'function') {
      try { chunkedRef.current.seek(delta, { relative: true }); } catch (_) { /* noop */ }
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    seekTo((a.currentTime || 0) + delta);
  }, [seekTo]);

  const setRate = useCallback((rate) => {
    if (!Number.isFinite(rate) || rate < 0.5 || rate > 3) return;
    if (chunkedRef.current && typeof chunkedRef.current.setPlaybackRate === 'function') {
      try { chunkedRef.current.setPlaybackRate(rate); } catch (_) { /* noop */ }
    }
    const a = audioRef.current;
    if (a) {
      a.playbackRate = rate;
      a.defaultPlaybackRate = rate;
    }
  }, []);

  // P3 ChunkedAudioManager bridge. EnhancedPlayerPage calls this whenever it
  // initializes (or tears down) a chunked engine. The provider mirrors the
  // manager's timeupdate / play / pause / ended events into its own state so
  // the modal + mini-player scrubber stay accurate for multi-voice books.
  const setChunkedManager = useCallback((manager) => {
    // Detach previous listeners
    const prev = chunkedRef.current;
    if (prev && chunkedListenersRef.current.length) {
      chunkedListenersRef.current.forEach(([type, fn]) => {
        try { prev.removeEventListener(type, fn); } catch (_) { /* noop */ }
      });
    }
    chunkedListenersRef.current = [];

    if (!manager) {
      chunkedRef.current = null;
      setIsChunked(false);
      // Reset display state — the standard audio element will take over next.
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      return;
    }

    chunkedRef.current = manager;
    setIsChunked(true);

    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      try { callbacksRef.current.onPause && callbacksRef.current.onPause(); } catch (e) { console.error('[PlayerContext] chunked onPause cb error', e); }
    };
    const onEnded = () => {
      setIsPlaying(false);
      try { callbacksRef.current.onEnded && callbacksRef.current.onEnded(); } catch (e) { console.error('[PlayerContext] chunked onEnded cb error', e); }
    };
    const onTimeUpdate = (e) => {
      const t = e?.detail?.currentTime || 0;
      const d = e?.detail?.duration || 0;
      setCurrentTime(t);
      if (d && d !== duration) setDuration(d);
      try { callbacksRef.current.onTimeUpdate && callbacksRef.current.onTimeUpdate(t, d); } catch (err) { console.error('[PlayerContext] chunked onTimeUpdate cb error', err); }
    };

    manager.addEventListener('play', onPlay);
    manager.addEventListener('pause', onPause);
    manager.addEventListener('ended', onEnded);
    manager.addEventListener('timeupdate', onTimeUpdate);
    chunkedListenersRef.current = [
      ['play', onPlay], ['pause', onPause], ['ended', onEnded], ['timeupdate', onTimeUpdate],
    ];
  }, []);

  // Global audio event handlers — keep concerns minimal: update state + invoke
  // any page-supplied callbacks. Page-side logic (progress save, auto-next
  // chapter, audio-status messages) lives in the consumer via callbacks.
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => {
    setIsPlaying(false);
    try { callbacksRef.current.onPause && callbacksRef.current.onPause(); } catch (e) { console.error('[PlayerContext] onPause cb error', e); }
  };
  const handleTimeUpdate = (e) => {
    const t = e.currentTarget.currentTime || 0;
    const d = e.currentTarget.duration || 0;
    setCurrentTime(t);
    if (d !== duration) setDuration(d);
    try { callbacksRef.current.onTimeUpdate && callbacksRef.current.onTimeUpdate(t, d); } catch (err) { console.error('[PlayerContext] onTimeUpdate cb error', err); }
  };
  const handleLoadedMetadata = (e) => {
    setDuration(e.currentTarget.duration || 0);
  };
  const handleEnded = () => {
    setIsPlaying(false);
    try { callbacksRef.current.onEnded && callbacksRef.current.onEnded(); } catch (err) { console.error('[PlayerContext] onEnded cb error', err); }
  };

  // Wire to MediaSession at provider level so lock-screen / Control-Center
  // controls work from any route (not only on /player).
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    if (!nowPlaying) return;
    try {
      const artwork = nowPlaying.cover
        ? [{ src: nowPlaying.cover, sizes: '512x512', type: 'image/png' }]
        : [{ src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }];
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: nowPlaying.chapter || nowPlaying.title || 'VoxSmyth',
        artist: nowPlaying.author || 'VoxSmyth',
        album: nowPlaying.title || '',
        artwork,
      });
      navigator.mediaSession.setActionHandler('play', () => play());
      navigator.mediaSession.setActionHandler('pause', () => pause());
      navigator.mediaSession.setActionHandler('seekbackward', (e) => seekRelative(-(e?.seekOffset || 10)));
      navigator.mediaSession.setActionHandler('seekforward', (e) => seekRelative(e?.seekOffset || 10));
      // Lock-screen / Control Center previous/next track buttons step
      // chapters Audible-style. We dispatch the same `vs-player-command`
      // events the FullPlayerModal uses so EnhancedPlayerPage's existing
      // handler does the actual chapter switch.
      try {
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          try { window.dispatchEvent(new CustomEvent('vs-player-command', { detail: { action: 'chapter-prev' } })); } catch (_) { /* noop */ }
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
          try { window.dispatchEvent(new CustomEvent('vs-player-command', { detail: { action: 'chapter-next' } })); } catch (_) { /* noop */ }
        });
      } catch (_) { /* older Safari ignores unknown actions */ }
    } catch (err) {
      // iOS Safari < 17.1 sometimes throws on MediaMetadata constructor.
      console.debug('[PlayerContext] MediaSession setup skipped:', err && err.message);
    }
  }, [nowPlaying, play, pause, seekRelative]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try { navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'; } catch (_) { /* noop */ }
  }, [isPlaying]);

  // Imperatively set iOS-only `playsInline` attribute on the <audio> ref —
  // React strips it from <audio> JSX (only allows it on <video>). This bit
  // is critical for iOS PWA background playback (without it iOS suspends
  // audio when the user backgrounds the app).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.playsInline = true;
      a.setAttribute('playsinline', '');
      a.setAttribute('webkit-playsinline', '');
    } catch (_) { /* old browsers */ }
  }, []);

  // Background audio continuation — keep MediaSession state consistent when
  // the page goes hidden (lock screen, tab switch, app background on iOS PWA).
  // Browsers will keep audio playing as long as `<audio>` has `playsInline`
  // and MediaSession metadata is set. We re-assert state on visibility change
  // because iOS occasionally drops it.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      try {
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
        }
      } catch (_) { /* noop */ }
    };
    const onPageShow = () => onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [isPlaying]);

  // Test hook — exposes provider methods to Playwright in non-production
  // builds so the ChunkedAudioManager bridge can be verified with a stub.
  // Gated to dev only by checking the bundled NODE_ENV flag.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Skip ONLY in production builds — webpack inlines NODE_ENV at build time.
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return undefined;
    window.__vsPlayerDebug = {
      setChunkedManager,
      setNowPlaying,
      play,
      pause,
      togglePlay,
      seekTo,
      seekRelative,
      setRate,
      getState: () => ({ isPlaying, currentTime, duration, isChunked, nowPlaying }),
    };
    return () => { try { delete window.__vsPlayerDebug; } catch (_) { /* noop */ } };
  }, [setChunkedManager, setNowPlaying, play, pause, togglePlay, seekTo, seekRelative, setRate, isPlaying, currentTime, duration, isChunked, nowPlaying]);

  const value = {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    nowPlaying,
    isChunked,
    setNowPlaying,
    setAudioCallbacks,
    setChunkedManager,
    play,
    pause,
    togglePlay,
    seekTo,
    seekRelative,
    setRate,
  };

  return (
    <PlayerContext.Provider value={value}>
      {/* Persistent global audio element. Hidden — visible UI is rendered by
          the player page or the mini-player. `playsInline` (set imperatively
          via ref above) is essential for iOS PWA background playback (the
          audio keeps streaming when the screen locks / user backgrounds the
          app). `preload="auto"` lets the browser buffer ahead so the modal
          scrubber can show duration immediately after src changes. */}
      <audio
        ref={audioRef}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="auto"
        style={{ display: 'none' }}
        data-testid="global-audio-element"
      />
      {children}
    </PlayerContext.Provider>
  );
};

export default PlayerProvider;
